import React, { useState, useEffect, useContext } from 'react';
import { authFetch } from '../../Utilities/Api';
import AuthContext from '../../context/AuthContext';
import './EditProfile.css';

const EditProfile = () => {
    const { refreshProfile } = useContext(AuthContext);

    const [nickname, setNickname] = useState('');
    const [newUsername, setNewUsername] = useState('');
    const [bio, setBio] = useState('');
    const [location, setLocation] = useState('');

    const [profileFile, setProfileFile] = useState(null);
    const [coverFile, setCoverFile] = useState(null);

    const [usernameMessage, setUsernameMessage] = useState('');
    const [nicknameMessage, setNicknameMessage] = useState('');
    const [profileMessage, setProfileMessage] = useState('');
    const [coverMessage, setCoverMessage] = useState('');
    const [bioMessage, setBioMessage] = useState('');
    const [locationMessage, setLocationMessage] = useState('');

    // 1. Initial Load
    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const res = await authFetch('/users/profile');
                const data = await res.json();
                if (res.ok) {
                    setNickname(data.nickname || '');
                    setBio(data.bio || '');
                    setLocation(data.location || '');
                }
            } catch (err) {
                console.error('❌ Error loading profile:', err);
            }
        };
        fetchProfile();
    }, []);

    // --- HELPER: Universal Field Updater ---
    // This matches your exports.updateField in userController.js
    const updateProfileField = async (fieldName, value, setMessage) => {
        setMessage('');
        try {
            const res = await authFetch('/users/update-fields', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ [fieldName]: value }),
            });
            const data = await res.json();
            if (res.ok) {
                setMessage('✅ Saved');
                await refreshProfile();
            } else {
                setMessage(`❌ ${data.message || 'Error'}`);
            }
        } catch {
            setMessage('❌ Server Error');
        }
    };

    // --- HANDLERS ---

    const handleProfilePictureUpload = async () => {
        setProfileMessage('');
        if (!profileFile) return setProfileMessage('❌ Select image');

        try {
            const formData = new FormData();
            formData.append('avatar', profileFile);

            const res = await authFetch('/upload/avatar-image', {
                method: 'POST',
                body: formData,
            });
            const data = await res.json();
            if (res.ok) {
                setProfileMessage('✅ Uploaded');
                setProfileFile(null);
                await refreshProfile();
            } else {
                setProfileMessage(`❌ ${data.message}`);
            }
        } catch { setProfileMessage('Error'); }
    };

    const handleCoverPictureUpload = async () => {
        setCoverMessage('');
        if (!coverFile) return setCoverMessage('❌ Select image');

        try {
            const formData = new FormData();
            formData.append('cover', coverFile);

            const res = await authFetch('/upload/cover-image', {
                method: 'POST',
                body: formData,
            });
            const data = await res.json();
            if (res.ok) {
                setCoverMessage('✅ Uploaded');
                setCoverFile(null);
                await refreshProfile();
            } else {
                setCoverMessage(`❌ ${data.message}`);
            }
        } catch { setCoverMessage('Error'); }
    };

    const handleNicknameChange = () => updateProfileField('nickname', nickname, setNicknameMessage);
    const handleBioChange = () => updateProfileField('bio', bio, setBioMessage);
    const handleLocationChange = () => updateProfileField('location', location, setLocationMessage);

    const handleUsernameChange = async () => {
        setUsernameMessage('');
        if (!newUsername.trim()) return setUsernameMessage('❌ Empty');

        try {
            const res = await authFetch('/users/change-username', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username: newUsername }),
            });
            const data = await res.json();
            if (res.ok) {
                setUsernameMessage(`✅ Updated`);
                setNewUsername('');
                await refreshProfile();
            } else {
                setUsernameMessage(`❌ ${data.message}`);
            }
        } catch { setUsernameMessage('Error'); }
    };

    return (
        <div className="edit-content-only">
            {/* Avatar Row */}
            <div className="edit-row">
                <div className="edit-input-group">
                    <label>Profile Picture</label>
                    <input
                        type="file"
                        accept="image/*"
                        className="file-input"
                        onChange={(e) => setProfileFile(e.target.files[0])}
                    />
                </div>
                <button className="btn-action" onClick={handleProfilePictureUpload}>Upload</button>
                {profileMessage && <span className="status-msg">{profileMessage}</span>}
            </div>

            {/* Cover Photo Row */}
            <div className="edit-row">
                <div className="edit-input-group">
                    <label>Cover Photo</label>
                    <input
                        type="file"
                        accept="image/*"
                        className="file-input"
                        onChange={(e) => setCoverFile(e.target.files[0])}
                    />
                </div>
                <button className="btn-action" onClick={handleCoverPictureUpload}>Upload</button>
                {coverMessage && <span className="status-msg">{coverMessage}</span>}
            </div>

            {/* Nickname Row */}
            <div className="edit-row">
                <div className="edit-input-group">
                    <label>Nickname</label>
                    <input
                        type="text"
                        value={nickname}
                        onChange={(e) => setNickname(e.target.value)}
                        placeholder="Display Name"
                    />
                </div>
                <button className="btn-action" onClick={handleNicknameChange}>Save</button>
                {nicknameMessage && <span className="status-msg">{nicknameMessage}</span>}
            </div>

            {/* Bio Row */}
            <div className="edit-row">
                <div className="edit-input-group full-width">
                    <label>Bio</label>
                    <textarea
                        rows="2"
                        value={bio}
                        onChange={(e) => setBio(e.target.value)}
                        placeholder="About you..."
                    />
                </div>
                <button className="btn-action self-end" onClick={handleBioChange}>Save</button>
                {bioMessage && <span className="status-msg">{bioMessage}</span>}
            </div>

            {/* Location Row */}
            <div className="edit-row">
                <div className="edit-input-group">
                    <label>Location</label>
                    <input
                        type="text"
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                        placeholder="City, Country"
                    />
                </div>
                <button className="btn-action" onClick={handleLocationChange}>Save</button>
                {locationMessage && <span className="status-msg">{locationMessage}</span>}
            </div>

            {/* Username Row */}
            <div className="edit-row">
                <div className="edit-input-group">
                    <label>Username</label>
                    <input
                        type="text"
                        value={newUsername}
                        onChange={(e) => setNewUsername(e.target.value)}
                        placeholder="New Username"
                    />
                </div>
                <button className="btn-action" onClick={handleUsernameChange}>Update</button>
                {usernameMessage && <span className="status-msg">{usernameMessage}</span>}
            </div>
        </div>
    );
};

export default EditProfile;